import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Clock, Calendar, Loader2 } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import api from "@/lib/api";

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/blog/posts/${slug}`)
      .then(r => { setPost(r.data); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
    </div>
  );

  if (notFound) return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Nav />
      <div className="max-w-2xl mx-auto px-6 pt-40 text-center">
        <h1 className="font-display text-4xl text-stone-900">Post not found</h1>
        <Link to="/blog" className="mt-8 inline-flex items-center gap-2 text-indigo-600 hover:underline">
          <ArrowLeft className="w-4 h-4" /> Back to Blog
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="blog-post-page">
      <Helmet>
        <title>{`${post.title} — LoomPDF Blog`}</title>
        <meta name="description" content={post.meta_description} />
        <link rel="canonical" href={`https://loompdf.com/blog/${post.slug}`} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.meta_description} />
        <meta property="og:image" content={post.cover_image_url || "https://loompdf.com/logo.jpg"} />
        <meta property="og:type" content="article" />
      </Helmet>
      <Nav />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 pt-24 pb-20" data-testid="blog-post-article">
        {/* Back */}
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-stone-400 hover:text-indigo-600 transition-colors mb-8" data-testid="blog-back-link">
          <ArrowLeft className="w-4 h-4" /> All articles
        </Link>

        {/* Cover image */}
        {post.cover_image_url && (
          <div className="rounded-2xl overflow-hidden mb-8 bg-stone-100" style={{ maxHeight: "420px" }}>
            <img src={post.cover_image_url} alt={post.title} className="w-full h-full object-cover" />
          </div>
        )}

        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center gap-4 text-xs text-stone-400 mb-4">
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{fmt(post.published_at)}</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{post.reading_time} min read</span>
            <span className="text-stone-300">·</span>
            <span>{post.author}</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-stone-900 leading-tight">
            {post.title}
          </h1>
          <p className="text-stone-500 text-lg mt-4 leading-relaxed">{post.meta_description}</p>
        </header>

        {/* Content */}
        <div
          className="blog-content text-stone-700 text-base leading-relaxed"
          dangerouslySetInnerHTML={{ __html: post.content }}
          data-testid="blog-post-content"
        />

        {/* Footer CTA */}
        <div className="mt-16 p-8 rounded-2xl bg-indigo-50 border border-indigo-100 text-center">
          <p className="font-display text-xl text-stone-900">Ready to study smarter?</p>
          <p className="text-stone-500 text-sm mt-2 mb-5">Upload any PDF and get a structured study deck in under 60 seconds — free.</p>
          <Link to="/" className="btn-primary inline-flex items-center gap-2" data-testid="blog-cta-link">
            Try LoomPDF free
          </Link>
        </div>

        {/* Back */}
        <div className="mt-12 border-t border-stone-200 pt-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-stone-400 hover:text-indigo-600 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to all articles
          </Link>
        </div>
      </article>
      <Footer />
    </div>
  );
}
